import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";

const values = [
  { title: "Precision", desc: "We obsess over the details — proportion, materiality, light — because great architecture depends on getting the small things exactly right." },
  { title: "Authenticity", desc: "We don't impose a signature style. Every project should feel inevitable — the perfect solution for that site, client, and program." },
  { title: "Transparency", desc: "Our clients understand every decision we make. We believe better-informed clients lead to better architecture." },
  { title: "Craft", desc: "We are hands-on through construction, because the design is only as good as its execution." },
];

const team = [
  {
    name: "Marcus Rivera",
    role: "Principal Architect",
    bio: "Marcus founded Studio 1323 after a decade at SOM and a boutique residential practice in Venice. He holds an M.Arch from UCLA and is licensed in California.",
    image: "https://public.readdy.ai/ai/img_res/dd36db9cbb53cdd3982183279f293b20.jpg",
  },
  {
    name: "Leila Nakamura",
    role: "Design Director",
    bio: "Leila leads design development on all Studio 1323 projects. Before joining the firm, she practiced in Tokyo and San Francisco, bringing a rigorous international perspective.",
    image: "https://readdy.ai/api/search-image?query=professional%20female%20architect%20portrait%2C%20intelligent%20serene%20expression%2C%20modern%20studio%20background%2C%20natural%20light%2C%20professional%20headshot%2C%2030s%2C%20asian%20heritage%2C%20architectural%20office&width=400&height=500&seq=23&orientation=portrait",
  },
  {
    name: "Diego Patel",
    role: "Project Manager",
    bio: "Diego ensures every Studio 1323 project is delivered on time and on budget. With 12 years of construction administration experience, nothing gets past him.",
    image: "https://readdy.ai/api/search-image?query=professional%20male%20project%20manager%20portrait%2C%20friendly%20confident%20expression%2C%20modern%20office%20background%2C%20natural%20light%2C%20professional%20headshot%2C%2030s%2C%20south%20asian%20heritage&width=400&height=500&seq=24&orientation=portrait",
  },
];

export default function About() {
  return (
    <div className="bg-arch-cream font-sans">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative h-[60vh] min-h-[480px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20architecture%20studio%20exterior%20santa%20monica%20california%2C%20clean%20minimal%20building%20facade%2C%20white%20concrete%2C%20evening%20light%2C%20professional%20photography&width=1920&height=900&seq=25&orientation=landscape"
              alt="Studio 1323 Santa Monica architecture firm"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
          </div>
          <div className="relative z-10 px-8 lg:px-14 pb-14 w-full">
            <p className="text-white/50 text-xs tracking-[0.25em] uppercase mb-3">About the Firm</p>
            <h1 className="font-serif text-4xl lg:text-6xl text-white font-light leading-tight max-w-2xl">
              Building architecture<br />that earns its place.
            </h1>
          </div>
        </section>

        {/* Story */}
        <section className="py-20 lg:py-28 px-8 lg:px-14 bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start max-w-6xl">
            <div>
              <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-4">Our Story</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-arch-charcoal font-light leading-tight mb-6">
                Fifteen years of<br />craft in Santa Monica
              </h2>
            </div>
            <div className="space-y-5">
              <p className="text-arch-gray text-sm font-light leading-relaxed">
                Studio 1323 was founded in 2010 with a simple premise: that great architecture requires deep attention to the specific — the specific site, the specific client, the specific program. Not an imposed aesthetic, but a rigorous search for the right answer.
              </p>
              <p className="text-arch-gray text-sm font-light leading-relaxed">
                Over fifteen years, that commitment has produced a body of work across Santa Monica, Los Angeles, and the greater Westside — from intimate hillside homes to 24-unit mixed-use developments to boutique hotels on the coast.
              </p>
              <p className="text-arch-gray text-sm font-light leading-relaxed">
                Today, Studio 1323 remains a tightly focused, partner-led practice. Every project benefits from the principals&apos; direct involvement, from the first concept sketch through final construction observation.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 lg:py-28 px-8 lg:px-14 bg-arch-cream">
          <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-10">What We Believe</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v) => (
              <div key={v.title} className="border-t border-arch-border pt-6">
                <h3 className="font-serif text-2xl text-arch-charcoal font-light mb-3">{v.title}</h3>
                <p className="text-arch-gray text-sm font-light leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Team */}
        <section className="py-20 lg:py-28 px-8 lg:px-14 bg-white">
          <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-10">The Team</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {team.map((member) => (
              <div key={member.name}>
                <div className="w-full h-72 overflow-hidden rounded-lg mb-5">
                  <img src={member.image} alt={member.name + " Studio 1323"} className="w-full h-full object-cover object-top" />
                </div>
                <p className="text-arch-charcoal text-sm font-semibold tracking-wide">{member.name}</p>
                <p className="text-arch-light text-xs uppercase tracking-wider mt-0.5 mb-3">{member.role}</p>
                <p className="text-arch-gray text-sm font-light leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-8 lg:px-14 bg-[#111111] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl text-white font-light">Ready to work together?</h2>
            <p className="text-white/50 text-sm font-light mt-2">Initial consultations are complimentary. Santa Monica &amp; Los Angeles.</p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-arch-charcoal px-7 py-3.5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-white/90 transition-all rounded-md cursor-pointer whitespace-nowrap"
          >
            Request Consultation <i className="ri-arrow-right-line" />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
